"use client";

import Img from '@/components/Img';
import { useState, useMemo, useEffect, useCallback } from "react";
import type { PriceCar } from "@/lib/fleet-server";
import { useLocale } from "@/lib/locale-context";
import { carSpecLabel } from "@/lib/car-spec-i18n";

// Витрина «Цены на аренду» — по образцу /prices Солнышко (таблица по классам +
// фильтр классов), акцент синий. Отличие: единый сайт — сверху КАРУСЕЛЬ ГОРОДОВ,
// при смене города клиент перезапрашивает автопарк выбранного города; ниже —
// КАРУСЕЛЬ КЛАССОВ (чипсы-фильтр). SEO — автоматически (у страницы нет настроек).
const ACCENT = "#0039A6";
const ACCENT_SOFT = "#EAF0FB";

type CityOpt = { station: string; name: string; name_en?: string; currency?: string };

// Иконки классов (монохром-чёрные SVG): на активном чипсе перекрашиваются в
// белый через CSS-фильтр brightness(0) invert(1).
const CLASS_ICONS: Record<string, string> = {
  "Люкс": "/images/filters/lux.svg",
  "Бизнес": "/images/filters/business.svg",
  "Комфорт класс": "/images/filters/comfort.svg",
  "Промо": "/images/filters/promo.svg",
  "Внедорожник": "/images/filters/jeep.svg",
  "Кроссоверы": "/images/filters/suv.svg",
  "Минивены": "/images/filters/van.svg",
  "Кабриолет": "/images/filters/cab.svg",
};
const classIconStyle = (active: boolean): React.CSSProperties => ({
  width: 18, height: 18, flexShrink: 0,
  filter: active ? "brightness(0) invert(1)" : "none",
});

interface Props {
  cars: PriceCar[];
  cities: CityOpt[];
  initialStation: string;
}

function useIsMobile(): boolean {
  const [m, setM] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return m;
}

export default function PricesPageClient({ cars: initialCars, cities, initialStation }: Props) {
  const { t, locale } = useLocale();
  const seatsLabel = (n: number): string => t("unit.seats", { n: String(n) });
  const isMobile = useIsMobile();
  const [station, setStation] = useState(initialStation);
  const [cars, setCars] = useState<PriceCar[]>(initialCars);
  const [loading, setLoading] = useState(false);
  const [classFilter, setClassFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"price" | "year" | "name">("price");
  const cur = cities.find((c) => c.station === station)?.currency || "₽";

  const selectCity = useCallback(async (st: string) => {
    if (st === station || loading) return;
    setStation(st);
    setClassFilter("all");
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/fleet/${st}`, { cache: "no-store" });
      const d = res.ok ? await res.json() : {};
      const mapped: PriceCar[] = (d.cars || []).map((c: any) => ({
        guid: c.guid, name: c.name,
        rentPrice: Math.round((c.rentPrice || 0) / 100),
        deposit: Math.round((c.deposit || 0) / 100),
        year: c.yearOfManufacture || 0,
        transmission: c.typeOfTransmission || "",
        fuelType: (c.isHybrid || (c.batteryCapacity || 0) > 0) ? "Гибрид" : (c.fuelType || ""), engineVolume: c.engineVolume || "",
        seats: c.seatCount || 5, bags: c.numberOfBags || 0,
        carClass: c.carClass || "", unit: c.unit || "",
        discount1: parseFloat(c.discount1 || "0") || 0,
        discount2: parseFloat(c.discount2 || "0") || 0,
        minAge: c.minAge || 0, minDrivingExperience: c.minDrivingExperience || 0,
        photos: c.photos || [],
      })).filter((c: PriceCar) => c.rentPrice > 0);
      setCars(mapped);
    } catch {
      setCars([]);
    } finally {
      setLoading(false);
    }
  }, [station, loading]);

  // Дедуп по названию модели: оставляем новее год / дешевле / больше фото.
  const dedupedCars = useMemo(() => {
    const map = new Map<string, PriceCar>();
    for (const c of cars) {
      const key = c.name.trim().toLowerCase();
      const ex = map.get(key);
      if (!ex) { map.set(key, c); continue; }
      const newer = c.year > ex.year;
      const cheaper = c.year === ex.year && c.rentPrice < ex.rentPrice;
      const morePhotos = c.year === ex.year && c.rentPrice === ex.rentPrice && c.photos.length > ex.photos.length;
      if (newer || cheaper || morePhotos) map.set(key, c);
    }
    return Array.from(map.values());
  }, [cars]);

  const classes = useMemo(() => {
    const set = new Set(dedupedCars.map((c) => c.carClass).filter(Boolean));
    return Array.from(set).sort();
  }, [dedupedCars]);

  const filtered = useMemo(() => {
    let arr = classFilter === "all" ? dedupedCars : dedupedCars.filter((c) => c.carClass === classFilter);
    arr = [...arr].sort((a, b) => {
      if (sortBy === "price") return a.rentPrice - b.rentPrice;
      if (sortBy === "year") return b.year - a.year;
      return a.name.localeCompare(b.name);
    });
    return arr;
  }, [dedupedCars, classFilter, sortBy]);

  const chip = (active: boolean): React.CSSProperties => ({
    flexShrink: 0, padding: "10px 18px", borderRadius: 999, cursor: "pointer",
    border: active ? `1px solid ${ACCENT}` : "1px solid #E0E0E5",
    background: active ? ACCENT : "#fff", color: active ? "#fff" : "#333",
    fontSize: 14, fontWeight: active ? 700 : 500, fontFamily: "inherit", whiteSpace: "nowrap",
    display: "inline-flex", alignItems: "center", gap: 8,
  });

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "60px 20px 85px" }}>
      <h1 style={{ fontSize: 36, fontWeight: "bold", color: "#000", marginBottom: 0 }}>{t("prices.h1")}</h1>
      <div style={{ width: 80, height: 3, background: ACCENT, margin: "20px 0 28px" }} />

      {/* Карусель городов */}
      {cities.length > 1 && (
        <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 12, marginBottom: 24, WebkitOverflowScrolling: "touch" }}>
          {cities.map((c) => {
            const active = c.station === station;
            return (
              <button
                key={c.station}
                type="button"
                onClick={() => selectCity(c.station)}
                style={{
                  flexShrink: 0, padding: "10px 20px", borderRadius: 24, cursor: "pointer",
                  fontSize: 15, fontWeight: active ? 700 : 500, fontFamily: "inherit",
                  border: active ? `1px solid ${ACCENT}` : "1px solid #E0E0E5",
                  background: active ? ACCENT : "#fff", color: active ? "#fff" : "#1E1E2D",
                }}
              >
                {locale === "en" && (c as { name_en?: string }).name_en ? (c as { name_en?: string }).name_en : c.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Карусель классов + сортировка */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: isMobile ? "nowrap" : "wrap", overflowX: isMobile ? "auto" : "visible", paddingBottom: isMobile ? 8 : 0 }}>
        <button onClick={() => setClassFilter("all")} style={chip(classFilter === "all")}>
          <Img src="/images/filters/comfort.svg" alt="" style={classIconStyle(classFilter === "all")} />
          {t("prices.allClasses")} ({dedupedCars.length})
        </button>
        {classes.map((cls) => {
          const count = dedupedCars.filter((c) => c.carClass === cls).length;
          const icon = CLASS_ICONS[cls];
          const active = classFilter === cls;
          return (
            <button key={cls} onClick={() => setClassFilter(cls)} style={chip(active)}>
              {icon && (
                <Img src={icon} alt="" style={classIconStyle(active)} />
              )}
              {carSpecLabel(cls, locale)} ({count})
            </button>
          );
        })}
        {!isMobile && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 13, color: "#888" }}>{t("prices.sortBy")}</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #E0E0E5", fontSize: 13, fontFamily: "inherit", cursor: "pointer" }}>
              <option value="price">{t("prices.sortPrice")}</option>
              <option value="year">{t("prices.sortYear")}</option>
              <option value="name">{t("prices.sortName")}</option>
            </select>
          </div>
        )}
      </div>

      {loading && <div style={{ padding: "24px 0", color: "#8E8E93", fontSize: 15 }}>{t("prices.loadingFleet")}</div>}

      {/* Mobile: карточки */}
      {isMobile && !loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map((car) => {
            const price3d = car.discount1 > 0 ? Math.round(car.rentPrice * (1 - car.discount1 / 100)) : car.rentPrice;
            const price7d = car.discount2 > 0 ? Math.round(car.rentPrice * (1 - car.discount2 / 100)) : car.rentPrice;
            return (
              <div key={car.guid} style={{ background: "#fff", borderRadius: 12, border: "1px solid #EDEDF0", padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  {car.photos[0] && (
                    <Img src={car.photos[0]} alt={car.name} style={{ width: 90, height: 60, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, color: "#1E1E2D", lineHeight: 1.25 }}>{car.name}</div>
                    {car.carClass && <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{carSpecLabel(car.carClass, locale)} · {carSpecLabel(car.transmission, locale)} · {car.year || ""}</div>}
                    <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                      {seatsLabel(car.seats || 0)}{car.minAge > 0 ? ` · ${t("prices.colAge")}: ${car.minAge}` : ""}{car.minDrivingExperience > 0 ? ` · ${t("prices.colExp")}: ${car.minDrivingExperience}` : ""}
                    </div>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                  <PriceCell label={t("prices.cell1day")} value={car.rentPrice} accent={false} currency={cur} />
                  <PriceCell label={t("prices.cell3days")} value={price3d} accent={car.discount1 > 0} currency={cur} />
                  <PriceCell label={t("prices.cell7days")} value={price7d} accent={car.discount2 > 0} bold currency={cur} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Desktop: таблица */}
      {!isMobile && !loading && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #EDEDF0", overflowX: "auto" }}>
          <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ background: "#F9F9FB", borderBottom: "2px solid #EDEDF0" }}>
                <th style={thStyle}>{t("prices.colCar")}</th>
                <th style={{ ...thStyle, textAlign: "center" }}>{t("prices.colYear")}</th>
                <th style={{ ...thStyle, textAlign: "center" }}>{t("prices.colSeats")}</th>
                <th style={{ ...thStyle, textAlign: "center" }}>{t("prices.colAge")}</th>
                <th style={{ ...thStyle, textAlign: "center" }}>{t("prices.colExp")}</th>
                <th style={{ ...thStyle, textAlign: "right" }}>{t("prices.col1day")}</th>
                <th style={{ ...thStyle, textAlign: "right", background: "#F2F6FF" }}>{t("prices.col3days")}</th>
                <th style={{ ...thStyle, textAlign: "right", background: ACCENT_SOFT }}>{t("prices.col7days")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((car, i) => {
                const price3d = car.discount1 > 0 ? Math.round(car.rentPrice * (1 - car.discount1 / 100)) : car.rentPrice;
                const price7d = car.discount2 > 0 ? Math.round(car.rentPrice * (1 - car.discount2 / 100)) : car.rentPrice;
                return (
                  <tr key={car.guid} style={{ borderBottom: "1px solid #F5F5F7", background: i % 2 ? "#FAFAFA" : "#fff" }}>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        {car.photos[0] && (
                          <Img src={car.photos[0]} alt={car.name} style={{ width: 72, height: 48, objectFit: "cover", borderRadius: 6, flexShrink: 0 }} />
                        )}
                        <div>
                          <div style={{ fontWeight: 600, color: "#1E1E2D" }}>{car.name}</div>
                          {car.carClass && <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{carSpecLabel(car.carClass, locale)} · {carSpecLabel(car.transmission, locale)}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={tdCenter}>{car.year || "—"}</td>
                    <td style={tdCenter}>{seatsLabel(car.seats || 0)}</td>
                    <td style={tdCenter}>{car.minAge > 0 ? `${car.minAge}` : "—"}</td>
                    <td style={tdCenter}>{car.minDrivingExperience > 0 ? `${car.minDrivingExperience}` : "—"}</td>
                    <td style={tdPrice}><span style={{ fontSize: 15, fontWeight: 600, color: "#1E1E2D" }}>{car.rentPrice.toLocaleString("ru")} {cur}</span></td>
                    <td style={{ ...tdPrice, background: "#F2F6FF" }}><span style={{ fontSize: 15, fontWeight: 600, color: car.discount1 > 0 ? ACCENT : "#999" }}>{price3d.toLocaleString("ru")} {cur}</span></td>
                    <td style={{ ...tdPrice, background: ACCENT_SOFT }}><span style={{ fontSize: 15, fontWeight: 700, color: car.discount2 > 0 ? ACCENT : "#999" }}>{price7d.toLocaleString("ru")} {cur}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", color: "#888" }}>{t("prices.empty")}</div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = { padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: "0.3px" };
const tdCenter: React.CSSProperties = { padding: "12px 8px", textAlign: "center", color: "#555" };
const tdPrice: React.CSSProperties = { padding: "12px 12px", textAlign: "right" };

function PriceCell({ label, value, accent, bold, currency }: { label: string; value: number; accent: boolean; bold?: boolean; currency?: string }) {
  return (
    <div style={{ background: accent ? ACCENT_SOFT : "#F9F9FB", borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
      <div style={{ fontSize: 10, color: "#888", marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: bold ? 700 : 600, color: accent ? ACCENT : "#1E1E2D", whiteSpace: "nowrap" }}>{value.toLocaleString("ru")} {currency || "₽"}</div>
    </div>
  );
}
